/**
 * Date helpers. DB stores unix seconds (integer). Form inputs use the native
 * <input type="date"> which gives "YYYY-MM-DD" strings.
 *
 * Convention: date-only values store as midnight UTC unix seconds. Reasonable
 * for a Kansas-only team — see plan §8 Q9 (no cross-state residency). If the
 * team ever spans time zones we'll have to revisit and store an explicit zone.
 */

/** "YYYY-MM-DD" → unix seconds, midnight UTC. */
export function dateInputToUnix(s: string | null | undefined): number | null {
  if (!s) return null;
  // Build a YYYY-MM-DDT00:00:00Z string to avoid local-time parsing.
  const d = new Date(`${s}T00:00:00Z`);
  const ts = d.getTime();
  return Number.isFinite(ts) ? Math.floor(ts / 1000) : null;
}

/** Unix seconds → "YYYY-MM-DD" suitable for <input type="date" value=...>. */
export function unixToDateInput(unix: number | null | undefined): string {
  if (unix === null || unix === undefined) return '';
  const d = new Date(unix * 1000);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Unix seconds → "05/22/2026".
 *
 * D-1 (Daniel 2026-08-10): the team asked for every date to read mm/dd/yyyy.
 * This used to render "May 22, 2026". The month name was never ambiguous, but
 * it sat next to native date inputs that render in the BROWSER's locale, so a
 * browser set to English (UK) showed 07/09/2026 meaning September 7 while the
 * text beside it said "Sep 7, 2026". Two formats on one screen is how somebody
 * eventually reads a closing date wrong. One numeric format everywhere.
 *
 * The locale is pinned to 'en-US' rather than the viewer's, deliberately: a
 * date on a Kansas real estate file means the same thing regardless of what
 * language the laptop is set to.
 */
export function formatDate(unix: number | null | undefined): string {
  if (unix === null || unix === undefined) return '—';
  return new Date(unix * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });
}

/** Unix seconds → "05/22/2026 at 4:31 PM" (in viewer's local timezone). */
export function formatDateTime(unix: number | null | undefined): string {
  if (unix === null || unix === undefined) return '—';
  const d = new Date(unix * 1000);
  const date = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time}`;
}

/** Unix seconds → "2 days ago" / "in 3 days". */
export function formatRelative(unix: number | null | undefined): string {
  if (unix === null || unix === undefined) return '—';
  const diffMs = unix * 1000 - Date.now();
  const days = Math.round(diffMs / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0) return `in ${days} days`;
  return `${-days} days ago`;
}
